/**
 * Comments panel + composer (UX Pack F5b, docked-panel restyle F5c). The
 * panel is a right-docked non-modal drawer ported from the CRM
 * design-reviewer's annotations panel: numbered accent-striped cards, an
 * "Open only" filter, and bi-directional pin↔card linking (the canvas stays
 * interactive underneath — that is the point of not being a modal).
 * Kit-built (createDrawerShell / createButton / createConfirmDialog — no
 * native dialogs), shared by the builder and the review shell via the
 * controller's capability flags: guests see everything but get no
 * resolve/delete affordances (the modules simply don't render them — and the
 * server refuses regardless).
 *
 * Orphaned threads — anchor no longer resolves against the viewed version —
 * stay DISCLOSED: dashed card, warning note, full conversation, reply box.
 * They are numbered but never get a guessed pin.
 */

import { eventBus } from '../core/events.js';
import { store } from '../core/state.js';
import {
  createModal,
  createDrawerShell,
  createButton,
  createConfirmDialog,
  copyTextToClipboard
} from './components.js';
import { relativeTime } from '../shared/format.js';
import { illustration } from './illustrations.js';
import { mountCommentRail } from './comment-rail.js';
import { mountCommentMinimap } from './comment-minimap.js';
import { buildReviewMarkdown, buildReviewJson, reviewPacketBasename } from './comment-export.js';
import {
  getCommentThreads,
  getThreadNumbers,
  getActiveCommentThread,
  setActiveCommentThread,
  activateThreadAndNavigate,
  resolveThreadAnchor,
  replyToThread,
  setThreadStatus,
  deleteThread,
  createThreadAt,
  getCommentCapabilities,
  setCommentPolling,
  markCommentsSeen,
  isCommentsDisabled,
  isCommentModeActive,
  setCommentMode
} from './comment-controller.js';

/** The one live panel: { close, focusThread } | null. */
let _panel = null;

/**
 * Save text to a file. Same anchor+object-URL dance the ZIP export uses
 * (shell.js), including the deferred revoke — Safari cancels the download if
 * the URL dies before the click is processed.
 */
function downloadTextFile(filename, text, mime) {
  const url = URL.createObjectURL(new Blob([text], { type: `${mime};charset=utf-8` }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    URL.revokeObjectURL(url);
    a.remove();
  }, 100);
}

/**
 * Plain-text rendering of a thread list for the clipboard.
 *
 * Numbering is the STABLE sequence, not the position in this list: the CRM
 * numbers by raw array index and so emits "1., 3., 6." whenever a filter is
 * on, which reads like missing comments rather than hidden ones. Each line
 * also names its page, because threads are project-wide and a pasted list
 * outlives the panel that produced it.
 *
 * @param {Array<object>} threads  already in display order
 * @param {(id: string) => string} [pageNameFor]
 * @returns {string}
 */
export function formatThreadsForClipboard(threads, pageNameFor = null) {
  const numbers = getThreadNumbers();
  const lines = [];
  for (const thread of threads) {
    const [first, ...replies] = thread.comments || [];
    const page = pageNameFor?.(thread.pageId) || thread.pageId || 'this page';
    const status = thread.status === 'resolved' ? ' [resolved]' : '';
    lines.push(
      `${numbers.get(thread.id) ?? '?'}. ${first?.authorName || 'Comment'} · ${page}${status}`
    );
    lines.push(`   ${first?.body || '(no comment)'}`);
    for (const reply of replies) {
      lines.push(`   ↳ ${reply.authorName}: ${reply.body}`);
    }
    lines.push('');
  }
  return lines.join('\n').trimEnd();
}

function commentCard(thread, number, { showToast, canPan, replyState }) {
  const capabilities = getCommentCapabilities();
  const card = document.createElement('li');
  card.className = 'pb-comment-card';
  card.dataset.threadId = thread.id;
  card.tabIndex = 0;

  const resolution = resolveThreadAnchor(thread);
  if (resolution.kind === 'orphaned') card.classList.add('is-orphaned');
  if (thread.status === 'resolved') card.classList.add('is-resolved');
  if (thread.id === getActiveCommentThread()) card.classList.add('is-active');

  const avatar = document.createElement('span');
  avatar.className = 'pb-comment-card-avatar';
  avatar.textContent = String(number || '');
  avatar.setAttribute('aria-hidden', 'true');
  card.appendChild(avatar);

  const cardBody = document.createElement('div');
  cardBody.className = 'pb-comment-card-body';
  card.appendChild(cardBody);

  const [first, ...replies] = thread.comments || [];

  const head = document.createElement('div');
  head.className = 'pb-comment-card-head';
  const author = document.createElement('span');
  author.className = 'pb-comment-author';
  author.textContent = first?.authorName || 'Comment';
  if (first?.authorKind === 'guest') {
    const chip = document.createElement('span');
    chip.className = 'pb-comment-guest-chip';
    chip.textContent = 'Reviewer';
    author.appendChild(chip);
  }
  head.appendChild(author);
  const when = document.createElement('span');
  when.className = 'pb-comment-date';
  when.textContent = relativeTime(thread.createdAt);
  head.appendChild(when);
  if (thread.status === 'resolved') {
    const tag = document.createElement('span');
    tag.className = 'pb-comment-resolved-tag';
    tag.textContent = 'Resolved';
    head.appendChild(tag);
  }
  if (resolution.kind === 'orphaned' || resolution.kind === 'pending') {
    const note = document.createElement('span');
    note.className = 'pb-comment-anchor-note';
    note.textContent =
      resolution.kind === 'pending'
        ? 'Locating pinned element…'
        : 'Pinned element no longer exists in this version';
    head.appendChild(note);
  }
  cardBody.appendChild(head);

  const body = document.createElement('p');
  body.className = 'pb-comment-body';
  body.textContent = first?.body || ''; // textContent — bodies are plain text
  cardBody.appendChild(body);

  if (replies.length) {
    const list = document.createElement('ul');
    list.className = 'pb-comment-replies';
    for (const comment of replies) {
      const message = document.createElement('li');
      message.className = 'pb-comment-message';
      const replyAuthor = document.createElement('span');
      replyAuthor.className = 'pb-comment-author';
      replyAuthor.textContent = comment.authorName;
      if (comment.authorKind === 'guest') {
        const chip = document.createElement('span');
        chip.className = 'pb-comment-guest-chip';
        chip.textContent = 'Reviewer';
        replyAuthor.appendChild(chip);
      }
      const replyBody = document.createElement('p');
      replyBody.className = 'pb-comment-body';
      replyBody.textContent = comment.body;
      message.appendChild(replyAuthor);
      message.appendChild(replyBody);
      list.appendChild(message);
    }
    cardBody.appendChild(list);
  }

  // Collapsible reply form (CRM .dr-rev-reply-form). Open state + draft
  // survive re-renders via replyState, keyed by thread id.
  const replyForm = document.createElement('div');
  replyForm.className = 'pb-comment-reply-form';
  const draft = replyState.get(thread.id);
  replyForm.hidden = draft === undefined;
  const replyInput = document.createElement('textarea');
  replyInput.className = 'pb-input';
  replyInput.rows = 2;
  replyInput.placeholder = 'Reply…';
  replyInput.maxLength = 4000;
  if (draft !== undefined) replyInput.value = draft;
  replyInput.addEventListener('input', () => replyState.set(thread.id, replyInput.value));
  replyForm.appendChild(replyInput);
  const replyActions = document.createElement('div');
  replyActions.className = 'pb-comment-reply-form-actions';
  replyActions.appendChild(
    createButton({
      variant: 'ghost',
      size: 'sm',
      label: 'Cancel',
      onClick: () => {
        replyState.delete(thread.id);
        replyForm.hidden = true;
        replyInput.value = '';
      }
    })
  );
  replyActions.appendChild(
    createButton({
      variant: 'primary',
      size: 'sm',
      label: 'Send',
      errorScope: 'Comment reply',
      onAsyncClick: async () => {
        const value = replyInput.value.trim();
        if (!value) {
          replyInput.focus();
          return;
        }
        await replyToThread(thread.id, value);
        replyState.delete(thread.id);
      }
    })
  );
  replyForm.appendChild(replyActions);

  const actions = document.createElement('div');
  actions.className = 'pb-comment-card-actions';
  if (capabilities.canResolve) {
    actions.appendChild(
      createButton({
        variant: 'ghost',
        size: 'sm',
        label: thread.status === 'resolved' ? 'Reopen' : 'Resolve',
        errorScope: 'Comment status',
        onAsyncClick: async () => {
          await setThreadStatus(thread.id, thread.status === 'resolved' ? 'open' : 'resolved');
        }
      })
    );
  }
  actions.appendChild(
    createButton({
      variant: 'ghost',
      size: 'sm',
      label: 'Reply',
      onClick: () => {
        if (replyForm.hidden) {
          replyState.set(thread.id, replyInput.value);
          replyForm.hidden = false;
          replyInput.focus();
        } else {
          replyState.delete(thread.id);
          replyForm.hidden = true;
        }
      }
    })
  );
  if (capabilities.canDelete) {
    actions.appendChild(
      createButton({
        variant: 'ghost',
        size: 'sm',
        label: 'Delete',
        errorScope: 'Comment delete',
        onAsyncClick: async () => {
          await createConfirmDialog({
            title: 'Delete thread',
            message: 'Delete this comment thread?',
            details: 'The whole conversation is removed for everyone. This cannot be undone.',
            confirmLabel: 'Delete',
            danger: true,
            errorScope: 'Comment delete',
            onConfirm: async () => {
              await deleteThread(thread.id);
              showToast?.('Thread deleted', { type: 'success' });
            }
          });
        }
      })
    );
  }
  cardBody.appendChild(actions);
  cardBody.appendChild(replyForm);

  // Card activation: highlight the pin + pan the canvas. Clicks landing on
  // inner controls (buttons, the reply textarea) are NOT activations.
  // One shared route for every activation surface — it also switches PAGE when
  // the thread lives on another one, which a bare scroll message cannot do.
  const activate = () => {
    void activateThreadAndNavigate(thread, { pan: canPan });
  };
  card.addEventListener('click', (e) => {
    if (e.target.closest('button, input, textarea, a')) return;
    activate();
  });
  card.addEventListener('keydown', (e) => {
    if (e.target !== card) return;
    if (e.key !== 'Enter' && e.key !== ' ') return;
    e.preventDefault();
    activate();
  });

  return card;
}

/**
 * The docked panel — one instance at a time; polling runs only while it is
 * open (the panel owns that switch). Calling this while open focuses
 * `focusThreadId` instead of no-opping. `variant: 'review'` (guest shell)
 * offsets below the wrapping review topbar and never pans (no bridge there).
 */
/**
 * Step the open panel's selection. Returns false when there is no panel, so a
 * keyboard shortcut can decline to swallow the key rather than silently
 * eating J/K while the comments surface is closed.
 * @param {number} delta
 * @returns {boolean} whether the step was handled
 */
export function stepCommentSelection(delta) {
  if (!_panel?.step) return false;
  _panel.step(delta);
  return true;
}

/** Collapsed is a global preference, not per project. '1' | absent (shell.js). */
const COLLAPSE_KEY = 'pb:comments-collapsed';

function readCollapsedPref() {
  try {
    return localStorage.getItem(COLLAPSE_KEY) === '1';
  } catch {
    return false; // private mode: the panel simply opens expanded
  }
}

function writeCollapsedPref(collapsed) {
  try {
    if (collapsed) localStorage.setItem(COLLAPSE_KEY, '1');
    else localStorage.removeItem(COLLAPSE_KEY);
  } catch {
    /* optional preference */
  }
}

export function showCommentsPanel({ showToast = null, focusThreadId = null, variant = null } = {}) {
  if (_panel) {
    if (focusThreadId) _panel.focusThread(focusThreadId);
    return _panel;
  }
  if (isCommentsDisabled()) {
    showToast?.('Review comments are not enabled on this server', { type: 'info' });
    return;
  }
  const canPan = variant !== 'review';
  const previousFocus = document.activeElement;
  const { drawer, titleEl, closeBtn, body } = createDrawerShell({
    id: 'comments',
    title: 'Comments',
    closeLabel: 'Close comments'
  });
  drawer.classList.add('pb-comments-panel');
  if (variant === 'review') drawer.classList.add('pb-comments-panel--review');
  drawer.setAttribute('data-testid', 'comments-panel');

  const count = document.createElement('span');
  count.className = 'pb-comments-count';
  titleEl.appendChild(count);

  // ── Collapse: hand the width back to the canvas, keep a reopen pill ──
  const collapseBtn = createButton({
    variant: 'ghost',
    size: 'sm',
    label: '›',
    className: 'pb-comments-collapse-btn',
    onClick: () => setCollapsed(true)
  });
  collapseBtn.setAttribute('aria-label', 'Collapse comments panel');
  collapseBtn.setAttribute('aria-controls', drawer.id || 'pb-comments-panel');
  closeBtn.parentElement?.insertBefore(collapseBtn, closeBtn);

  const pill = createButton({
    variant: 'ghost',
    size: 'sm',
    label: 'Comments',
    className: 'pb-comments-pill',
    onClick: () => setCollapsed(false)
  });
  const pillCount = document.createElement('span');
  pillCount.className = 'pb-comments-count';
  pill.appendChild(pillCount);
  pill.hidden = true;
  pill.setAttribute('aria-expanded', 'false');
  pill.setAttribute('aria-controls', drawer.id || 'pb-comments-panel');
  document.body.appendChild(pill);

  /**
   * Zeroing the width token on the ROOT is what slides the rail (and later the
   * minimap) outward — they position against it, so neither needs to know the
   * panel collapsed. Same trick as the CRM's --dr-panel-w.
   */
  function setCollapsed(collapsed) {
    drawer.hidden = collapsed;
    pill.hidden = !collapsed;
    pill.setAttribute('aria-expanded', String(!collapsed));
    document.documentElement.classList.toggle('pb-comments-collapsed', collapsed);
    writeCollapsedPref(collapsed);
    if (!collapsed) {
      drawer.focus();
    } else {
      pill.focus();
    }
  }

  let filterOpenOnly = false;
  const replyState = new Map(); // threadId → draft (present = form open)
  /** The order the stepper walks: whatever is on screen, in sequence order. */
  let visibleThreads = [];

  // ── Stepper: walk the VISIBLE list, so the arrows agree with the filter ──
  const nav = document.createElement('div');
  nav.className = 'pb-comments-nav';
  const prevBtn = createButton({
    variant: 'ghost',
    size: 'sm',
    label: '‹',
    className: 'pb-comments-nav-btn',
    onClick: () => step(-1)
  });
  prevBtn.setAttribute('aria-label', 'Previous comment');
  const posLabel = document.createElement('span');
  posLabel.className = 'pb-comments-nav-pos';
  const nextBtn = createButton({
    variant: 'ghost',
    size: 'sm',
    label: '›',
    className: 'pb-comments-nav-btn',
    onClick: () => step(1)
  });
  nextBtn.setAttribute('aria-label', 'Next comment');
  nav.appendChild(prevBtn);
  nav.appendChild(posLabel);
  nav.appendChild(nextBtn);
  titleEl.parentElement?.insertBefore(nav, titleEl.nextSibling);

  /**
   * Move `delta` through the visible threads. Entering from either end when
   * nothing is selected, and NOT wrapping — the CRM's behaviour, and the one
   * that makes "did I reach the end?" answerable.
   */
  function step(delta) {
    if (!visibleThreads.length) return;
    const activeId = getActiveCommentThread();
    const at = visibleThreads.findIndex((t) => t.id === activeId);
    let next;
    if (at === -1) next = delta > 0 ? 0 : visibleThreads.length - 1;
    else next = Math.min(visibleThreads.length - 1, Math.max(0, at + delta));
    const thread = visibleThreads[next];
    if (thread) void activateThreadAndNavigate(thread, { pan: canPan });
  }

  function syncNav() {
    const activeId = getActiveCommentThread();
    const at = visibleThreads.findIndex((t) => t.id === activeId);
    const total = visibleThreads.length;
    posLabel.textContent = total ? `${at === -1 ? '–' : at + 1} / ${total}` : '0 / 0';
    prevBtn.disabled = total === 0 || at === 0;
    nextBtn.disabled = total === 0 || (at !== -1 && at === total - 1);
  }

  const toolbar = document.createElement('div');
  toolbar.className = 'pb-comments-toolbar';

  // The panel's primary action, and the ONLY comment-mode control in the
  // builder — the Share menu used to carry a second one that opened this panel
  // as a side effect. Arming lives beside the threads it produces, so the
  // unseen count and the way to add to it are the same surface.
  //
  // Guests never see it: their shell composes from its own frame handler and
  // has no bridge to intercept (`canPin`). State is not tracked locally —
  // `setCommentMode` emits `comments:changed`, which re-runs `render()`, so a
  // toggle from the mobile bar, the command palette or a shortcut lands here
  // too.
  const pinBtn = getCommentCapabilities().canPin
    ? createButton({
        variant: 'primary',
        size: 'sm',
        label: 'Add a comment',
        // A span, not a bare text node: `render()` rewrites this label on every
        // mode change, and textContent on the button would delete any icon a
        // later change adds. Same reason the Share control carries one.
        labelClass: 'pb-btn-label',
        className: 'pb-comments-pin-toggle',
        onClick: () => setCommentMode(!isCommentModeActive())
      })
    : null;
  if (pinBtn) {
    pinBtn.setAttribute('aria-pressed', 'false');
    toolbar.appendChild(pinBtn);
  }

  const filterBtn = createButton({
    variant: 'ghost',
    size: 'sm',
    label: 'Open only',
    className: 'pb-comments-filter',
    onClick: () => {
      filterOpenOnly = !filterOpenOnly;
      render();
    }
  });
  filterBtn.setAttribute('aria-pressed', 'false');
  toolbar.appendChild(filterBtn);
  const hint = document.createElement('span');
  hint.className = 'pb-comments-hint';
  toolbar.appendChild(hint);

  // Copy what is ON SCREEN — the filter is part of the user's intent. Numbers
  // stay the stable sequence, and each line names its page, so a pasted list
  // still means something away from the panel that produced it.
  const copyBtn = createButton({
    variant: 'ghost',
    size: 'sm',
    label: 'Copy',
    className: 'pb-comments-copy',
    errorScope: 'Comment copy',
    onAsyncClick: async () => {
      if (!visibleThreads.length) return;
      const copied = await copyTextToClipboard(
        formatThreadsForClipboard(visibleThreads, pageNameFor)
      );
      showToast?.(
        copied
          ? `Copied ${visibleThreads.length} comment${visibleThreads.length === 1 ? '' : 's'}`
          : 'Could not copy — your browser blocked clipboard access',
        { type: copied ? 'success' : 'info' }
      );
    }
  });
  copyBtn.setAttribute('aria-label', 'Copy the listed comments');
  toolbar.appendChild(copyBtn);

  // Export covers EVERY thread, unlike Copy — a packet that silently dropped
  // resolved threads would be a misleading record of the review.
  const exportBtn = createButton({
    variant: 'ghost',
    size: 'sm',
    label: 'Export',
    className: 'pb-comments-export',
    errorScope: 'Comment export',
    onAsyncClick: async () => {
      const all = getCommentThreads();
      if (!all.length) {
        showToast?.('No comments to export', { type: 'info' });
        return;
      }
      const state = store.getState();
      const meta = {
        projectName: state?.projectName || 'Project',
        version: Number(state?.cloudVersion) || null,
        numbers: getThreadNumbers(),
        pageNameFor,
        anchorFor: (thread) => {
          const kind = resolveThreadAnchor(thread).kind;
          return kind === 'element'
            ? 'an element'
            : kind === 'orphaned'
              ? 'an element that no longer exists (orphaned)'
              : 'this section';
        }
      };
      const ordered = all.sort(
        (a, b) => (meta.numbers.get(a.id) || 0) - (meta.numbers.get(b.id) || 0)
      );
      const base = reviewPacketBasename(meta.projectName, meta.version);
      downloadTextFile(`${base}.md`, buildReviewMarkdown(ordered, meta), 'text/markdown');
      downloadTextFile(
        `${base}.json`,
        JSON.stringify(buildReviewJson(ordered, meta), null, 2),
        'application/json'
      );
      showToast?.(`Exported ${ordered.length} comment${ordered.length === 1 ? '' : 's'}`, {
        type: 'success'
      });
    }
  });
  exportBtn.setAttribute('aria-label', 'Export all comments as markdown and JSON');
  toolbar.appendChild(exportBtn);
  body.appendChild(toolbar);

  const intro = document.createElement('p');
  intro.className = 'pb-comments-intro';
  intro.textContent =
    'Pin feedback to elements with Comment mode, or reply here. Orphaned threads point at elements this version no longer has.';
  body.appendChild(intro);

  const list = document.createElement('ul');
  list.className = 'pb-comments-list';
  body.appendChild(list);

  function cardFor(threadId) {
    for (const el of list.children) {
      if (el.dataset?.threadId === threadId) return el;
    }
    return null;
  }

  /** Human page name for the clipboard; falls back to the raw id. */
  function pageNameFor(pageId) {
    const pages = store.getState()?.pages || [];
    return pages.find((p) => p.id === pageId)?.name || pageId || '';
  }

  function hiddenByFilter(threadId) {
    return (
      filterOpenOnly &&
      getCommentThreads().some((thread) => thread.id === threadId && thread.status === 'resolved')
    );
  }

  function render() {
    const scrollTop = list.scrollTop;
    list.textContent = '';
    const numbers = getThreadNumbers();
    // Flat list in sequence order — the filter HIDES, it never renumbers.
    const threads = getCommentThreads().sort(
      (a, b) => (numbers.get(a.id) || 0) - (numbers.get(b.id) || 0)
    );
    const openCount = threads.filter((t) => t.status !== 'resolved').length;
    const resolvedCount = threads.length - openCount;
    const visible = filterOpenOnly ? threads.filter((t) => t.status !== 'resolved') : threads;

    count.textContent = String(openCount);
    pillCount.textContent = String(openCount);
    filterBtn.classList.toggle('is-active', filterOpenOnly);
    filterBtn.setAttribute('aria-pressed', String(filterOpenOnly));
    hint.textContent = filterOpenOnly && resolvedCount ? `${resolvedCount} resolved hidden` : '';

    // The mode is global and togglable from the mobile bar, the command palette
    // and a shortcut, so this reads it rather than tracking it. Label carries
    // the state as well as aria-pressed: the crosshair is hover-only, so a
    // touch user has no other way to see the mode is armed.
    if (pinBtn) {
      const armed = isCommentModeActive();
      pinBtn.classList.toggle('is-active', armed);
      pinBtn.setAttribute('aria-pressed', String(armed));
      const labelEl = pinBtn.querySelector('.pb-btn-label') || pinBtn;
      labelEl.textContent = armed ? 'Click an element…' : 'Add a comment';
    }

    // A filtered-away active thread is no longer highlightable anywhere.
    const activeId = getActiveCommentThread();
    if (activeId && filterOpenOnly && !visible.some((t) => t.id === activeId)) {
      setActiveCommentThread(null);
    }

    visibleThreads = visible;
    syncNav();

    for (const thread of visible) {
      list.appendChild(
        commentCard(thread, numbers.get(thread.id), { showToast, canPan, replyState })
      );
    }
    if (!visible.length) {
      // Two different empties: all-resolved is the FILTER hiding threads (and
      // gets a real way out of itself, CB-12b style), true-empty is the pane's
      // invitation. "Add a comment" is now this panel's own toolbar button a
      // few pixels up, so the copy names it rather than duplicating a control.
      const allResolved = threads.length > 0;
      const empty = document.createElement('li');
      empty.className = 'pb-comments-empty';
      const illo = document.createElement('div');
      illo.className = 'pb-illo pb-illo--sm';
      // Trusted static registry markup — must never route through escapeHTML.
      illo.innerHTML = illustration('comments', { variant: allResolved ? 'filtered' : 'empty' });
      if (illo.firstElementChild) empty.appendChild(illo);
      const emptyTitle = document.createElement('div');
      emptyTitle.className = 'pb-comments-empty-title';
      emptyTitle.textContent = allResolved ? 'Everything is resolved' : 'No comments yet';
      const emptyBody = document.createElement('div');
      emptyBody.className = 'pb-comments-empty-body';
      emptyBody.textContent = allResolved
        ? `${resolvedCount} resolved thread${resolvedCount === 1 ? ' is' : 's are'} hidden by the Open only filter.`
        : getCommentCapabilities().canPin
          ? 'Press “Add a comment” above, then click any element to pin the first one.'
          : 'Click anywhere on the page to leave the first one.';
      empty.append(emptyTitle, emptyBody);
      if (allResolved) {
        empty.appendChild(
          createButton({
            variant: 'ghost',
            size: 'sm',
            label: 'Show resolved',
            className: 'pb-comments-empty-cta',
            onClick: () => {
              filterOpenOnly = false;
              render();
            }
          })
        );
      }
      list.appendChild(empty);
    }
    list.scrollTop = scrollTop;
  }

  function focusThread(threadId) {
    if (!threadId) return;
    // Being sent to a thread while collapsed means expanding to show it.
    if (drawer.hidden) setCollapsed(false);
    if (hiddenByFilter(threadId)) {
      setActiveCommentThread(null);
      return;
    }
    setActiveCommentThread(threadId);
    // setActiveCommentThread no-ops the event when already active — make the
    // scroll unconditional.
    cardFor(threadId)?.scrollIntoView({ block: 'nearest' });
  }

  function onKeydown(e) {
    if (e.key !== 'Escape' || e.defaultPrevented) return;
    // A modal (composer, confirm) on top owns Escape.
    if (document.querySelector('.pb-modal-overlay')) return;
    close();
  }

  function close() {
    if (!_panel) return;
    _panel = null;
    document.removeEventListener('keydown', onKeydown);
    offChanged();
    offActive();
    rail?.destroy();
    minimap?.destroy();
    setCommentPolling(false);
    setActiveCommentThread(null);
    // Closing is not collapsing: drop the scope class so the rail's offset and
    // anything else keyed on the width token return to normal.
    document.documentElement.classList.remove('pb-comments-collapsed');
    pill.remove();
    drawer.remove();
    if (previousFocus && typeof previousFocus.focus === 'function' && previousFocus.isConnected) {
      previousFocus.focus();
    }
  }

  const offChanged = eventBus.on('comments:changed', render);
  const offActive = eventBus.on('comments:active-thread', ({ threadId } = {}) => {
    if (threadId && hiddenByFilter(threadId)) {
      setActiveCommentThread(null);
      return;
    }
    for (const el of list.children) {
      el.classList?.toggle('is-active', !!threadId && el.dataset?.threadId === threadId);
    }
    syncNav(); // the "3 / 6" counter follows whatever became active
    if (threadId) cardFor(threadId)?.scrollIntoView({ block: 'nearest' });
  });
  closeBtn.addEventListener('click', close);
  document.addEventListener('keydown', onKeydown);

  document.body.appendChild(drawer);
  render();
  setCommentPolling(true);
  markCommentsSeen();

  // The rail only means anything where there is a canvas to point at, so the
  // guest review shell (no pins, no positions feed) does not get one.
  const goToThread = (threadId) => {
    const thread = getCommentThreads().find((t) => t.id === threadId);
    if (thread) void activateThreadAndNavigate(thread, { pan: canPan });
  };
  const rail = canPan ? mountCommentRail({ onActivate: goToThread }) : null;
  const minimap = canPan
    ? mountCommentMinimap({
        onActivate: goToThread,
        // Lazy: page-shot pulls the whole export/bundler graph, which has no
        // business on the panel's import path when the map may never render.
        loadShot: async ({ projectId, version, pageId, signal }) => {
          const [{ ensurePageShot, isShotsUnavailable }] = await Promise.all([
            import('../preview/page-shot.js')
          ]);
          if (isShotsUnavailable()) return null;
          const { blob } = await ensurePageShot({
            projectId,
            version,
            pageId,
            state: store.getState(),
            assetSource: null,
            signal
          });
          return blob;
        }
      })
    : null;

  _panel = { close, focusThread, step, setCollapsed };
  drawer.tabIndex = -1;
  // Honour the stored preference, but a request to focus a specific thread
  // means the user is going somewhere — expand for that.
  setCollapsed(focusThreadId ? false : readCollapsedPref());
  if (focusThreadId) focusThread(focusThreadId);
  return _panel;
}

/** Composer — opened by the controller's compose-request (comment-mode click). */
export function showCommentComposer({ instanceId, eid, pageId, showToast = null } = {}) {
  const { body, footer, close } = createModal({
    title: eid ? 'Comment on this element' : 'Comment on this section',
    size: 'sm',
    overlayClassName: 'pb-comment-composer-overlay',
    testid: 'comment-composer',
    initialFocus: '.pb-comment-composer-input'
  });
  const input = document.createElement('textarea');
  input.className = 'pb-input pb-comment-composer-input';
  input.rows = 4;
  input.maxLength = 4000;
  input.placeholder = 'What should change here?';
  body.appendChild(input);
  footer.appendChild(
    createButton({
      variant: 'primary',
      label: 'Add comment',
      errorScope: 'Comment create',
      onAsyncClick: async () => {
        const value = input.value.trim();
        if (!value) {
          input.focus();
          return;
        }
        await createThreadAt({ instanceId, eid, pageId, body: value });
        close();
        showToast?.('Comment pinned', { type: 'success' });
      }
    })
  );
  footer.appendChild(createButton({ variant: 'ghost', label: 'Cancel', onClick: close }));
  return { close };
}

/** Wire the compose-request + open-thread events once (builder or shell). */
export function initCommentSidebar({ showToast = null } = {}) {
  const offs = [
    eventBus.on('comments:compose-request', (payload) => {
      showCommentComposer({ ...payload, showToast });
    }),
    eventBus.on('comments:open-thread', ({ threadId } = {}) => {
      showCommentsPanel({ showToast, focusThreadId: threadId || null });
    })
  ];
  return () => {
    for (const off of offs) if (typeof off === 'function') off();
  };
}
